import { useState, useEffect } from 'react';
import { Card, Typography } from '@mui/material';
import styled from 'styled-components';

import { IconEye, IconEyeOff } from '@tabler/icons-react';

import { AdminUserDetailsInterface } from '../../Models/AdminUserControl/AdminUserControl';

import RequiredParametersHelper from '../../Utilities/Helpers/RequiredParametersHelper';

import { ButtonType } from '../../Utilities/Enums/ButtonTypeEnum';
import { ViewType } from '../../Utilities/Enums/ViewTypeEnum';
import { AdminUserField, AdminUserProfileField } from '../../Utilities/Enums/ParametersEnum';

import FormTextField from '../../CustomViews/Elements/FormTextField';
import CustomButton from '../../CustomViews/Elements/CustomButton';
import CustomDetailsCard from '../../CustomViews/Pages/CustomDetailsCard/CustomDetailsCard';

const FieldItemsContainer = styled(Card) <{ $darkMode?: boolean; }>`
    box-shadow: none !important;
    background: transparent !important;
    width: 100%;
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 30px;
`;

const FieldItemCard = styled(Card) <{ $darkMode?: boolean; }>`
    box-shadow: none !important;
    background: transparent !important;
    width: 100%;
`;

const FieldItemName = styled(Typography) <{ $darkMode?: boolean; }>`
    box-shadow: none !important;
    background: transparent !important;
    color: #878a99 !important;
    font-weight: 600 !important;
    font-size: 0.80rem !important;
    margin: 0px 0px 5px 0px !important;
`;

const FieldItemValue = styled(Typography) <{ $darkMode?: boolean; }>`
    box-shadow: none !important;
    background: transparent !important;
    font-weight: 500 !important;
    font-size: 16px !important;
`;

const FormFieldItemsContainer = styled(Card) <{ $darkMode?: boolean; }>`
    box-shadow: none !important;
    background: transparent !important;
    width: 100%;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
`;

export default function AdminUserAccountInformation(props: AdminUserDetailsInterface) {
    //Variables
    let { viewType, previewData, formData, setformData, errorMessages, DropdownData, dropdownFetchStatus, resetPassword, setresetPassword, handleResetPassword } = props;
    let requiredFields = RequiredParametersHelper.adminUserControl();

    //Use States
    const [showPassword, setShowPassword] = useState(false);

    const usernameProps = {
        name: AdminUserField.USERNAME,
        title: 'Username',
        fieldValue: formData?.[AdminUserField.USERNAME] ?? "",
        type: 'text',
        disabled: viewType != ViewType.ADD,
        required: requiredFields?.find((field: any) => field.keyName == AdminUserField.USERNAME)?.keyName != null,
        errorMessages: errorMessages,
        handleOnChange: (selectedValue: any) => {
            if(setformData) {
                setformData((prev: any) => ({ ...prev, [AdminUserField.USERNAME]: selectedValue }));
            }
        },
    },
        passwordProps = {
            name: AdminUserField.PASSWORD,
            title: 'New Password',
            fieldValue: formData?.[AdminUserField.PASSWORD] ?? "",
            type: (showPassword ? 'text' : 'password'),
            hasIcon: true,
            iconPosition: 'end',
            icon: (showPassword ? <IconEyeOff /> : <IconEye />),
            disabled: viewType != ViewType.ADD && !resetPassword,
            required: viewType != ViewType.ADD && !resetPassword ? false : requiredFields?.find((field: any) => field.keyName == AdminUserField.PASSWORD)?.keyName != null,
            errorMessages: errorMessages,
            onClickIconTrigger: () => {
                setShowPassword(!showPassword);
            },
            handleOnChange: (selectedValue: any) => {
                if(setformData) {
                    setformData((prev: any) => ({ ...prev, [AdminUserField.PASSWORD]: selectedValue }));
                }
            },
        },
        confirmPasswordProps = {
            name: AdminUserField.CONFIRM_PASSWORD,
            title: 'Confirm Password',
            fieldValue: formData?.[AdminUserField.CONFIRM_PASSWORD] ?? "",
            type: (showPassword ? 'text' : 'password'),
            hasIcon: true,
            iconPosition: 'end',
            icon: (showPassword ? <IconEyeOff /> : <IconEye />),
            disabled: viewType != ViewType.ADD && !resetPassword,
            required: viewType != ViewType.ADD && !resetPassword ? false : requiredFields?.find((field: any) => field.keyName == AdminUserField.CONFIRM_PASSWORD)?.keyName != null,
            errorMessages: errorMessages,
            onClickIconTrigger: () => {
                setShowPassword(!showPassword);
            },
            handleOnChange: (selectedValue: any) => {
                if(setformData) {
                    setformData((prev: any) => ({ ...prev, [AdminUserField.CONFIRM_PASSWORD]: selectedValue }));
                }
            },
        },
        userEmailProps = {
            name: AdminUserField.USER_EMAIL,
            title: 'User Email',
            fieldValue: formData?.[AdminUserField.USER_EMAIL] ?? "",
            type: 'text',
            required: requiredFields?.find((field: any) => field.keyName == AdminUserField.USER_EMAIL)?.keyName != null,
            errorMessages: errorMessages,
            handleOnChange: (selectedValue: any) => {
                if(setformData) {
                    setformData((prev: any) => ({ ...prev, [AdminUserField.USER_EMAIL]: selectedValue }));
                }
            },
        },
        userContactNumberProps = {
            name: AdminUserField.USER_CONTACT_NUMBER,
            title: 'User Contact Number',
            fieldValue: formData?.[AdminUserField.USER_CONTACT_NUMBER] ?? "",
            type: 'number',
            required: requiredFields?.find((field: any) => field.keyName == AdminUserField.USER_CONTACT_NUMBER)?.keyName != null,
            errorMessages: errorMessages,
            handleOnChange: (selectedValue: any) => {
                if(setformData) {
                    setformData((prev: any) => ({ ...prev, [AdminUserField.USER_CONTACT_NUMBER]: selectedValue }));
                }
            },
        };

    //Event Handlers

    //Rendering Functions

    const renderPreviewData = () => {
        return <FieldItemsContainer>
            <FieldItemCard>
                <FieldItemName>{usernameProps?.title ?? "--"}</FieldItemName>
                <FieldItemValue>{previewData?.[AdminUserField.USERNAME] ?? "--"}</FieldItemValue>
            </FieldItemCard>
            <FieldItemCard>
                <FieldItemName>{userEmailProps?.title ?? "--"}</FieldItemName>
                <FieldItemValue>{previewData?.[AdminUserField.USER_EMAIL] ?? "--"}</FieldItemValue>
            </FieldItemCard>
            <div />

            <FieldItemCard>
                <FieldItemName>Password</FieldItemName>
                <FieldItemValue>**********</FieldItemValue>
            </FieldItemCard>
            <FieldItemCard>
                <FieldItemName>{userContactNumberProps?.title ?? "--"}</FieldItemName>
                <FieldItemValue>{previewData?.[AdminUserField.USER_CONTACT_NUMBER] ?? "--"}</FieldItemValue>
            </FieldItemCard>
            <div />
        </FieldItemsContainer>
    }

    const renderDetailsForm = () => {
        return <FormFieldItemsContainer>
            <FormTextField {...usernameProps} />
            <FormTextField {...userEmailProps} />

            <FormTextField {...passwordProps} />
            <FormTextField {...userContactNumberProps} />

            <FormTextField {...confirmPasswordProps} />
            <div />

            {viewType != ViewType.ADD && <CustomButton
                btnType={!resetPassword ? ButtonType.PRIMARY : ButtonType.SUCCESS}
                title={!resetPassword ? 'Reset Password' : 'Update Password'}
                onClick={(e: any) => {
                    // if(setresetPassword) {
                    //     let _newresetPassword = !resetPassword;

                    //     if(!_newresetPassword && setformData) {
                    //         setformData((prev: any) => ({ ...prev, [AdminUserField.PASSWORD]: '', [AdminUserField.CONFIRM_PASSWORD]: '' }));
                    //     }
                    //     setresetPassword(_newresetPassword);

                        
                    // }
                    if(!resetPassword) {
                        if(setresetPassword) {
                            setresetPassword(true);
                        }
                    } else {
                        if(handleResetPassword) {
                            handleResetPassword();
                        }
                    }
                }} />}
        </FormFieldItemsContainer>
    }

    return <CustomDetailsCard
        title={"Account Information"} >
            {viewType == ViewType.PREVIEW && renderPreviewData()}
            {viewType != ViewType.PREVIEW && renderDetailsForm()}
        </CustomDetailsCard>
}
